import React from 'react'
import {useTranslation} from 'react-i18next'
import {
 DiscountType,
 ICartBreakupItem,
} from 'models/OrderManagement'
import {formatNumberForDisplay} from 'utils/helper'
import {
 getBreakupLabelKey,
 getDiscountBreakup,
 getDisplayDiscountAmount,
 getDisplayOrderTotal,
 getVisibleCartBreakup,
} from './orderTotalsSummaryUtils'
import {
 DiscountSummaryRow,
 SummaryAmount,
 SummaryRow,
} from './OrderTotalsSummaryParts'

type OrderTotalsSummaryProps = {
 cartBreakup?: ICartBreakupItem[]
 orderTotal?: number
 orderTotalBeforeDiscount?: number
 amountPayable?: number
 sellerCatalogueTotal?: number
 originalAmountToBePaid?: number
 showStrikethrough?: boolean
 paymentMode?: string | null
 discountType: DiscountType
 discountValue: number
 discountAmount: number
 discountEditable?: boolean
 onEditDiscount: () => void
}

const OrderTotalsSummary = ({
 cartBreakup = [],
 orderTotal,
 orderTotalBeforeDiscount,
 amountPayable,
 sellerCatalogueTotal,
 originalAmountToBePaid,
 showStrikethrough,
 paymentMode,
 discountType,
 discountValue,
 discountAmount,
 discountEditable = false,
 onEditDiscount,
}: OrderTotalsSummaryProps) => {
 const {t} = useTranslation()

 const discountBreakup = getDiscountBreakup(cartBreakup)
 const visibleCartBreakup =
  getVisibleCartBreakup(cartBreakup)
 const payable = Number(amountPayable ?? 0)
 const displayOrderTotal = getDisplayOrderTotal(
  orderTotalBeforeDiscount,
  orderTotal
 )
 const displayDiscountAmount = getDisplayDiscountAmount(
  discountAmount,
  discountBreakup
 )

 const getBreakupLabel = (row: ICartBreakupItem) => {
  const labelKey = getBreakupLabelKey(row)
  return labelKey ? t(labelKey) : row.name
 }

 const discountRowProps = {
  discountType,
  discountAmount: displayDiscountAmount,
  discountValue,
  discountEditable,
  onEditDiscount,
 }

 return (
  <div className='border border-[#E6EBF0] border-t-0 bg-[#F8FAFC]'>
   <SummaryRow
    label={t('orderManagement.orderTotalIncludingGst')}
   >
    <SummaryAmount
     value={displayOrderTotal}
     previousValue={sellerCatalogueTotal}
     showStrikethrough={showStrikethrough}
    />
   </SummaryRow>

   {visibleCartBreakup.map((row) =>
    row.key === 'discount' ? (
     <DiscountSummaryRow
      key={row.key}
      {...discountRowProps}
     />
    ) : (
     <SummaryRow key={row.key} label={getBreakupLabel(row)}>
      <span className='text-[#33393D] text-[15px] font-semibold'>
       {formatNumberForDisplay(Number(row.value))}
      </span>
     </SummaryRow>
    )
   )}

   <SummaryRow
    label={t('orderManagement.amountToBePaid')}
    subtitle={
     paymentMode ? (
      <p className='text-[#80868C] text-[12px]'>
       {paymentMode}
      </p>
     ) : undefined
    }
   >
    <SummaryAmount
     value={payable}
     previousValue={originalAmountToBePaid}
     showStrikethrough={showStrikethrough}
    />
   </SummaryRow>
  </div>
 )
}

export default OrderTotalsSummary
